import { motion } from "framer-motion";
import Container from "Components/Container/Container";
import {
  FilmSlate,
  GraduationCap,
  Clock,
  DeviceMobile,
  Certificate,
  Globe,
  Lightning,
  BookOpen,
  Users,
  Star,
  Play,
  Shield,
} from "@phosphor-icons/react";

const ICON_MAP = {
  film: FilmSlate,
  "graduation-cap": GraduationCap,
  clock: Clock,
  devices: DeviceMobile,
  certificate: Certificate,
  globe: Globe,
  lightning: Lightning,
  book: BookOpen,
  users: Users,
  star: Star,
  play: Play,
  shield: Shield,
};

const PlatformExperience = ({ data }) => {
  if (!data) return null;

  const { sectionTitle, tagline, stats, cards } = data;

  // Stats bar JSX extracted so we can drop it into the flex in one spot with an `order` class.
  const renderStatsBar = () => {
    if (!stats?.length) return null;
    const total = stats.length;
    const gridCls =
      total <= 2
        ? "grid-cols-1 sm:grid-cols-2"
        : total === 3
          ? "grid-cols-1 sm:grid-cols-3"
          : "grid-cols-2 md:grid-cols-4";

    // Per-cell border logic — draws dividers between cells without leaving a
    // stray border dangling when the layout wraps to a new row.
    const getCellBorders = (i) => {
      if (total <= 3) {
        return i < total - 1
          ? "border-b sm:border-b-0 sm:border-e border-white/10"
          : "";
      }
      // 4+ stats: 2x2 on mobile, 1x4 on md+
      const col = i % 2;
      const row = Math.floor(i / 2);
      const parts = [];
      if (row === 0) parts.push("border-b md:border-b-0 border-white/10");
      if (col === 0) parts.push("border-e border-white/10");
      if (i < total - 1) parts.push("md:border-e md:border-white/10");
      return parts.join(" ");
    };

    return (
      <div className="relative overflow-hidden rounded-2xl bg-[#1f1f22] border border-white/10">
        <div
          aria-hidden
          className="lg:hidden absolute -top-20 -end-20 w-56 h-56 rounded-full bg-primary/20 blur-3xl pointer-events-none"
        />
        <div className={`relative grid ${gridCls}`}>
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`text-center py-6 px-4 sm:py-8 md:px-6 ${getCellBorders(i)}`}
            >
              <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-none tracking-tight">
                {stat.value}
              </p>
              <span className="block w-6 h-0.5 bg-primary/70 rounded-full mx-auto mt-2.5" />
              <p className="text-[11px] sm:text-xs md:text-sm uppercase tracking-[0.12em] font-semibold text-gray-400 mt-2.5">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section>
      <Container>
        {/*
          Flex + `order` so mobile renders Title → Cards → Stats,
          while desktop keeps the original Stats → Title → Cards.
          gap-y controls the spacing so reordering doesn't leave odd whitespace.
        */}
        <div className="flex flex-col gap-y-8 lg:gap-y-0">
          {/* Stats Bar — last on mobile, first on desktop */}
          <div className="order-last lg:order-first">{renderStatsBar()}</div>

          {/* Section Title + Tagline — desktop restores the original asymmetric margins */}
          {(sectionTitle || tagline) && (
            <div className="text-center lg:mt-section-gap-lg lg:mb-section-title-gap-lg">
              {sectionTitle && (
                <h2 className="text-2xl lg:text-4xl font-bold text-white">
                  {sectionTitle}
                </h2>
              )}
              {tagline && (
                <p className="text-gray-400 mt-3 max-w-2xl mx-auto text-sm lg:text-base">
                  {tagline}
                </p>
              )}
            </div>
          )}

          {/* Differentiator Cards */}
          {cards?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {cards.map((card, i) => {
                const IconComponent = ICON_MAP[card.icon] || BookOpen;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="bg-grey/60 border border-gray-700/40 rounded-2xl p-6 lg:p-7 hover:border-primary/30 transition-colors duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                      <IconComponent
                        size={22}
                        weight="duotone"
                        className="text-primary"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {card.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default PlatformExperience;
